// useResultProgress.ts
import { reactive } from "vue";
import { Fetcher } from "@/services/Fetcher";
import services from "@/composable/services";
import { LocalApplicationResult } from "@/types/front/LocalApplicationResult";
import { Chunk, ProgressEntry } from "@/types/front/ResultProgress";

export function useResultProgress(
  withApplication: LocalApplicationResult,
  withLocale?: string,
): any {
  const application = withApplication;
  const locale
    = withLocale || services.internationalisationService.getLocale() || "en";

  // Créer un objet reactive directement, pas une instance de ResultProgress
  const result = reactive({
    progresses: {} as Record<string, ProgressEntry[]>,
    listDatatypes: [] as string[],
    listDatatypesNames: {} as Record<string, string>,
    totalProgress: 0,
    showProgress: false,
    application,
    locale,
    parseChunck: null as any,
  });

  function initProgressStructure(manifest: Record<string, string[]>) {
    result.showProgress = true;
    result.listDatatypes = Object.keys(manifest);

    // Créer un nouvel objet au lieu de modifier l'existant
    const newProgresses: Record<string, ProgressEntry[]> = {};
    Object.entries(manifest).forEach(([datatype, files]) => {
      const localName = services.internationalisationService.localeDataTypeIdName(application, { id: datatype, name: datatype });
      result.listDatatypesNames[datatype] = localName || datatype;
      newProgresses[datatype] = files.map(file => ({
        file,
        status: "PENDING",
      }));
    });

    // Remplacer l'objet entier pour que Vue détecte le changement
    result.progresses = newProgresses;
  }

  function markFileLoaded(datatype: string, fileName: string, ok = true) {
    const files = result.progresses[datatype];
    if (files) {
      // Créer un nouvel array pour que Vue détecte le changement
      result.progresses[datatype] = files.map(file =>
        file.file === fileName
          ? { ...file, status: ok ? "DONE" : "ERROR" }
          : file,
      );
    }
  }

  function parseInfoChunck(chunk: Chunk) {
    switch (chunk.result) {
      case "MANIFEST":
        if (chunk.params?.manifest) {
          initProgressStructure(chunk.params.manifest);
        }
        else {
          result.showProgress = true;
        }
        break;
      case "LOADED_DATA":
        markFileLoaded(chunk.params.dataName, chunk.params.fileName);
    }
  }

  function markFileError(
    datatype: string,
    fileName: string,
    message: string | string[],
    chunk: Chunk,
  ) {
    const files = result.progresses[datatype];
    if (files) {
      // Créer un nouvel array pour que Vue détecte le changement
      result.progresses[datatype] = files.map(file =>
        file.file === fileName
          ? { ...file, status: "ERROR", message }
          : file,
      );
    }
    console.error("error", chunk.result?.dataName, chunk.result?.fileName, chunk);
  }

  function parseErrorChunck(chunk: Chunk, _canBeShowAndReturnErrorsMessage: any[] = []) {
    const message = services.errorsService.getErrorsMessages(
      chunk.result?.message + ".message",
      chunk.result?.params,
      "errors-csv.",
    );
    markFileError(
      chunk.result?.dataName,
      chunk.result?.fileName,
      message,
      chunk,
    );
  }

  function parseProgressChunck(chunk: Chunk) {
    const val = Number(chunk?.result || 0) * 100;
    result.totalProgress = Math.min(Math.max(val, 0), 100);
  }

  function parseChunck(
    chunk: Chunk,
    _?: any,
    canBeShowAndReturnErrorsMessage?: any[],
  ) {
    // Show progress modal as soon as first chunk arrives
    result.showProgress = true;
    switch (chunk.type) {
      case Fetcher.TIME_OUT_ON_CHUNK:
        services.alertService.toastError(
          "La connexion semble interrompue. Un résultat vous sera envoyé par mail.",
        );
        break;
      case "REACTIVE_INFO":
        parseInfoChunck(chunk);
        break;
      case "REACTIVE_PROGRESS":
        parseProgressChunck(chunk);
        break;
      case "REACTIVE_ERROR":
        parseErrorChunck(chunk);
        break;
      default:
        parseErrorChunck(chunk, canBeShowAndReturnErrorsMessage);
    }
  }

  result.parseChunck = parseChunck;

  return result;
}
